import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { AUTOMATIC_SUPPORTED_UNITS_SCHEMA } from '@schuettflix/interfaces';
import { toAutomaticUnit } from '../utils/toAutomaticUnit.ts';
import { QuantityAndUnitInput } from '@/shared/inputs/QuantityAndUnitInput.tsx';
import { PriceAndTaxClassInput } from '@/shared/inputs/PriceAndTaxClassInput.tsx';
import { SelectOrganizationInput } from '@/shared/inputs/SelectOrganizationInput.tsx';
import { NegativePurchasePriceHint, ZeroPurchasePriceHint } from '@/shared/components/PriceInputHints.tsx';
import { EXCLUDED_PARTNER_ORGANIZATION_IDS } from '@/shared/constants/excludedPartnerOrganizations.ts';
import { TRANSPORT_EXCHANGE_VALUE } from '@/shared/constants/transportExchange.ts';
import { AutomaticProductsFormValues } from '../schema/getAutomaticProductsFormSchema.ts';
import { useAutomaticFormContext } from '@/shared/forms/AutomaticProductsForm/context/AutomaticFormContext.tsx';
import { Fieldset, FieldsetBody, FieldsetColumn, FieldsetLabel } from '../components/Fieldset.tsx';
import { useFormLocations } from '../hooks/useFormLocations.ts';
import { usePositionFormQuantities } from '@/shared/forms/AutomaticProductsForm/hooks/usePositionFormQuantities.ts';

export function PositionPurchaseTransportFieldset() {
    const methods = useFormContext<AutomaticProductsFormValues>();

    const { t } = useTranslation();
    const { defaultValuesLabels, taxClassOptions, customerOrganization } = useAutomaticFormContext();
    const {
        register,
        watch,
        setValue,
        getValues,
        formState: { defaultValues },
    } = methods;
    const { clearLocations } = useFormLocations(methods);
    const { onPurchaseTransportUnitChange } = usePositionFormQuantities(methods);

    const [
        purchaseTransportPrice,
        purchaseTransportUnit,
        currencyCode,
        showZeroPurchaseTransportPrice,
        carrierOrganizationId,
    ] = watch([
        'purchaseTransportPrice',
        'purchaseTransportUnit',
        'currencyCode',
        'showZeroPurchaseTransportPrice',
        'carrierOrganizationId',
    ]);

    const [carrierOrganizationName, setCarrierOrganizationName] = useState<string | undefined>(
        defaultValuesLabels?.carrierOrganization
    );

    const isCarrierCustomerOrg = carrierOrganizationId === customerOrganization.id;

    useEffect(() => {
        if (isCarrierCustomerOrg) {
            setValue('purchaseTransportPrice', 0);
            setValue('showZeroPurchaseTransportPrice', false);
        }
    }, [isCarrierCustomerOrg, setValue]);

    return (
        <Fieldset
            title={t('forms.automaticProductsForm.sections.carrier')}
            dataTest="position-purchase-transport-fieldset"
        >
            <FieldsetBody className="@sm:grid-cols-2 @2xl:grid-cols-8 grid-cols-1">
                <div className="@2xl:grid @2xl:col-span-5 @2xl:grid-cols-2 @2xl:gap-2 contents">
                    <FieldsetColumn
                        className="@2xl:col-span-1 col-span-full"
                        label={t('forms.automaticProductsForm.labels.carrier')}
                    >
                        <SelectOrganizationInput
                            key={defaultValues?.carrierOrganizationId}
                            {...register('carrierOrganizationId')}
                            defaultValue={defaultValues?.carrierOrganizationId}
                            defaultValueLabel={defaultValuesLabels?.carrierOrganization}
                            placeholder={t('product.addPosition.carrier.label')}
                            exclude={{ ids: EXCLUDED_PARTNER_ORGANIZATION_IDS }}
                            filters={{ organizationType: 'carrier' }}
                            dataTest="carrier-organization-select-input"
                            specialOptions={[
                                {
                                    value: TRANSPORT_EXCHANGE_VALUE,
                                    label: t('forms.inputs.partnerSelectionInput.transportExchange'),
                                },
                            ]}
                            onValueChange={org => {
                                const carrierOrganizationId = getValues('carrierOrganizationId');

                                if (org && carrierOrganizationId && typeof carrierOrganizationId === 'number') {
                                    clearLocations(carrierOrganizationId);
                                }

                                setCarrierOrganizationName(org?.name);
                            }}
                        />
                    </FieldsetColumn>
                    <FieldsetColumn className="col-span-1">
                        <div className="grid grid-cols-2 gap-1">
                            <FieldsetLabel>{t('forms.automaticProductsForm.labels.quantity')}</FieldsetLabel>
                            <FieldsetLabel>{t('forms.automaticProductsForm.labels.unit')}</FieldsetLabel>
                        </div>
                        <QuantityAndUnitInput
                            quantityProps={{ ...register('purchaseTransportQuantity'), disabled: true }}
                            unitProps={{
                                ...register('purchaseTransportUnit'),
                                supportedUnitNames: [...AUTOMATIC_SUPPORTED_UNITS_SCHEMA.options],
                                variant: 'lg',
                                onUnitChange: unit => onPurchaseTransportUnitChange(toAutomaticUnit(unit)),
                            }}
                        />
                    </FieldsetColumn>
                </div>

                <FieldsetColumn className="@2xl:col-span-3 col-span-1">
                    <div className="grid grid-cols-3 gap-1">
                        <FieldsetLabel className="col-span-2">
                            {t('forms.automaticProductsForm.labels.unitPrice')}
                        </FieldsetLabel>
                        <FieldsetLabel className="col-span-1">
                            {t('forms.automaticProductsForm.labels.taxClass')}
                        </FieldsetLabel>
                    </div>
                    <PriceAndTaxClassInput
                        priceProps={{
                            ...register('purchaseTransportPrice'),
                            unit: purchaseTransportUnit,
                            currencyCode,
                            disabled: isCarrierCustomerOrg,
                            testId: 'purchase-transport-price-input',
                        }}
                        taxClassProps={{
                            ...register('purchaseTransportTaxClassId'),
                            options: taxClassOptions,
                            testId: 'purchase-transport-tax-class-input',
                        }}
                    />
                </FieldsetColumn>

                {purchaseTransportPrice === 0 && !isCarrierCustomerOrg && (
                    <div className="@2xl:col-span-8 mt-3">
                        <ZeroPurchasePriceHint
                            partnerOrganizationName={carrierOrganizationName}
                            isBillingRelevant={showZeroPurchaseTransportPrice ?? false}
                            onClick={() =>
                                setValue('showZeroPurchaseTransportPrice', !(showZeroPurchaseTransportPrice ?? false))
                            }
                        />
                    </div>
                )}
                {Number(purchaseTransportPrice) < 0 && (
                    <div className="@2xl:col-span-8 mt-3">
                        <NegativePurchasePriceHint partnerOrganizationName={carrierOrganizationName} />
                    </div>
                )}
            </FieldsetBody>
        </Fieldset>
    );
}
